import { request } from '../utility/apiClients';
import { APIS_ENDPOINTS, APIS_METHOD } from '../resources/api-constants';
import { isTokenAvailable } from '../utility/helpers';

export interface CookiePreferences {
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
}

export const COOKIE_CONSENT_KEY = 'cookieConsent';

/**
 * Check if user has consented to analytics (single source of truth)
 */
export const hasAnalyticsConsent = (): boolean => {
    try {
        const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
        const prefs = stored ? (JSON.parse(stored) as CookiePreferences) : null;
        return prefs?.analytics === true;
    } catch {
        return false;
    }
};

/**
 * Get current cookie preferences from localStorage (no API call)
 */
export const getStoredCookiePreferences = (): CookiePreferences | null => {
    try {
        const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
        return stored ? (JSON.parse(stored) as CookiePreferences) : null;
    } catch {
        return null;
    }
};

/**
 * Save cookie preferences to backend (if user is authenticated)
 * Falls back to localStorage if not authenticated or if API call fails
 */
export const saveCookiePreferences = async (preferences: CookiePreferences): Promise<void> => {
    // Always save to localStorage first for immediate UX
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());

    // If user is authenticated, sync with backend
    if (isTokenAvailable()) {
        try {
            await request(
                APIS_METHOD.POST,
                APIS_ENDPOINTS.COOKIE_PREFERENCES,
                {
                    necessary: preferences.necessary,
                    analytics: preferences.analytics,
                    marketing: preferences.marketing,
                },
                false // Don't show loader for cookie preferences
            );
            // Mark as synced to prevent duplicate sync attempts
            sessionStorage.setItem('cookiePrefsSynced', 'true');
            console.log('Cookie preferences saved to backend');
        } catch (error) {
            // If backend save fails, preferences are still in localStorage
            // Log error but don't throw - localStorage is sufficient fallback
            console.warn('Failed to save cookie preferences to backend:', error);
        }
    }
};

/**
 * Load cookie preferences from backend (if user is authenticated)
 * Falls back to localStorage if not authenticated or if API call fails
 */
export const loadCookiePreferences = async (): Promise<CookiePreferences | null> => {
    // If user is authenticated, try to load from backend
    if (isTokenAvailable()) {
        try {
            const response = await request(
                APIS_METHOD.GET,
                APIS_ENDPOINTS.COOKIE_PREFERENCES,
                {},
                false // Don't show loader for cookie preferences
            );
            
            if (response?.data) {
                const backendPrefs: CookiePreferences = {
                    necessary: response.data.necessary ?? true,
                    analytics: response.data.analytics ?? false,
                    marketing: response.data.marketing ?? false,
                };
                
                // Sync localStorage with backend data
                localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(backendPrefs));
                if (response.data.updated_at) {
                    localStorage.setItem('cookieConsentDate', response.data.updated_at);
                }
                
                return backendPrefs;
            }
        } catch (error) {
            // If backend load fails, fall back to localStorage
            console.warn('Failed to load cookie preferences from backend, using localStorage:', error);
        }
    }

    // Fallback to localStorage
    const storedPrefs = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (storedPrefs) {
        try {
            return JSON.parse(storedPrefs) as CookiePreferences;
        } catch (e) {
            console.error('Error parsing stored cookie preferences:', e);
            return null;
        }
    }

    return null;
};

/**
 * Update cookie preferences (for authenticated users)
 * Updates both localStorage and backend
 */
export const updateCookiePreferences = async (preferences: CookiePreferences): Promise<void> => {
    // Always update localStorage first
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());

    // If user is authenticated, update backend
    if (isTokenAvailable()) {
        try {
            await request(
                APIS_METHOD.PATCH,
                APIS_ENDPOINTS.COOKIE_PREFERENCES,
                {
                    necessary: preferences.necessary,
                    analytics: preferences.analytics,
                    marketing: preferences.marketing,
                },
                false // Don't show loader for cookie preferences
            );
            // Mark as synced to prevent duplicate sync attempts
            sessionStorage.setItem('cookiePrefsSynced', 'true');
            console.log('Cookie preferences updated in backend');
        } catch (error) {
            console.warn('Failed to update cookie preferences in backend:', error);
            // Don't throw - localStorage update succeeded
        }
    }
};

/**
 * Sync localStorage preferences to backend when user logs in
 * This ensures preferences set before login are saved to backend
 * Only syncs if preferences exist and haven't been synced yet
 */
export const syncPreferencesToBackend = async (): Promise<void> => {
    if (!isTokenAvailable()) {
        return; // User not authenticated, nothing to sync
    }

    // Check if already synced in this session
    const hasSynced = sessionStorage.getItem('cookiePrefsSynced');
    if (hasSynced === 'true') {
        return; // Already synced, skip
    }

    const storedPrefs = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!storedPrefs) {
        return; // No preferences to sync
    }

    try {
        const preferences = JSON.parse(storedPrefs) as CookiePreferences;

        // Try to load from backend first to compare (avoid redundant POST)
        try {
            const response = await request(
                APIS_METHOD.GET,
                APIS_ENDPOINTS.COOKIE_PREFERENCES,
                {},
                false
            );

            if (response?.data) {
                const backendPrefs: CookiePreferences = {
                    necessary: response.data.necessary ?? true,
                    analytics: response.data.analytics ?? false,
                    marketing: response.data.marketing ?? false,
                };

                if (
                    backendPrefs.necessary === preferences.necessary &&
                    backendPrefs.analytics === preferences.analytics &&
                    backendPrefs.marketing === preferences.marketing
                ) {
                    sessionStorage.setItem('cookiePrefsSynced', 'true');
                    return; // Already in sync, no POST needed
                }
            }
        } catch {
            // Backend may not have preferences yet, continue to sync
        }

        // Sync localStorage preferences to backend (saveCookiePreferences sets cookiePrefsSynced)
        await saveCookiePreferences(preferences);
        console.log('Cookie preferences synced to backend after login');
    } catch (error) {
        console.warn('Failed to sync cookie preferences to backend:', error);
    }
};
